import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { ControlLogo } from "@/components/ControlLogo";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { formatKr, formatDate, formatAccount, formatOrg } from "@/lib/format";
import { vippsState } from "@/lib/vippsConstants";
import { cn } from "@/lib/utils";
import { Loader2, CheckCircle2, AlertTriangle, Smartphone, CreditCard, Landmark, ShieldCheck, RefreshCw, Info } from "lucide-react";

const PUBLIC = { skipActAs: true, skipWorkspace: true };
const FINAL_BAD = ["CANCELLED", "ABORTED", "EXPIRED", "TERMINATED", "FAILED"];
const POLL_MS = 2500;
const POLL_MAX = 24; // ~60 sekunder

const Shell = ({ children, accent }) => (
  <div className="min-h-screen bg-background auth-decor flex flex-col">
    <header className="px-5 py-4 sm:px-8 flex items-center justify-between">
      <ControlLogo size={30} tagline="Sikker betaling av faktura" />
      <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Betalingen behandles av Vipps MobilePay</span>
    </header>
    <main className="flex-1 flex items-start sm:items-center justify-center px-4 pb-10 pt-2 sm:pt-0">
      <div className="w-full max-w-lg">
        <div className="rounded-2xl border bg-card shadow-sm overflow-hidden" data-testid="pay-card">
          <div className="h-1.5" style={{ background: accent || "hsl(var(--primary))" }} aria-hidden="true" />
          <div className="p-6 sm:p-8">{children}</div>
        </div>
        <p className="mt-5 text-center text-xs text-muted-foreground">Controlle sender deg til Vipps for selve betalingen. Vi lagrer aldri kort- eller kontoopplysninger.</p>
      </div>
    </main>
  </div>
);

const Row = ({ label, value, testId, strong }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5"><span className="text-sm text-muted-foreground">{label}</span><span className={cn("text-sm tnum text-right", strong && "font-medium")} data-testid={testId}>{value}</span></div>
);

const StatusBanner = ({ tone, icon: Icon, title, children, testId }) => (
  <div className={cn("rounded-xl border px-4 py-3 flex items-start gap-3", tone)} data-testid={testId}>
    <Icon className="h-5 w-5 shrink-0 mt-0.5" />
    <div className="text-sm"><p className="font-medium">{title}</p>{children ? <p className="opacity-90">{children}</p> : null}</div>
  </div>
);

/** /betal/:token (kunden) og /betal/:token/retur (etter Vipps). Ingen innlogging. */
export default function PayPage() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isReturn = /\/retur\/?$/.test(location.pathname);
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState("");
  const [startError, setStartError] = useState("");
  const [polls, setPolls] = useState(0);
  const [checking, setChecking] = useState(isReturn);
  const timer = useRef(null);

  const fetchView = async (status = false) => {
    const r = await api.get(`/pay/${token}${status ? "/status" : ""}`, PUBLIC);
    setView(r.data);
    return r.data;
  };

  useEffect(() => {
    setView(null); setError(""); setPolls(0); setChecking(isReturn);
    fetchView(isReturn).catch((e) => setError(errorMessage(e, "Betalingslenken kunne ikke åpnes")));
    return () => clearTimeout(timer.current);
  }, [token, isReturn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retur fra Vipps: poll status til faktura er betalt, forsøket er avsluttet, eller vi gir opp (~60 s)
  useEffect(() => {
    if (!isReturn || !view) return undefined;
    const done = view.status === "paid" || FINAL_BAD.includes(view.payment_state) || view.status === "credited";
    if (done || polls >= POLL_MAX) { setChecking(false); return undefined; }
    timer.current = setTimeout(async () => {
      try { await fetchView(true); } catch { /* prøv igjen */ }
      setPolls((n) => n + 1);
    }, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [isReturn, view, polls]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (method) => {
    setStarting(method); setStartError("");
    try {
      const r = await api.post(`/pay/${token}/start`, { method }, PUBLIC);
      if (r.data.redirect_url) window.location.assign(r.data.redirect_url);
      else setStartError("Vipps ga ingen betalingsadresse. Prøv igjen.");
    } catch (e) {
      setStartError(errorMessage(e, "Kunne ikke starte betalingen"));
    } finally {
      setStarting("");
    }
  };

  const recheck = async () => { setChecking(true); setPolls(0); try { await fetchView(true); } catch { /* ignorér */ } };

  if (error) {
    return (
      <Shell>
        <Alert variant="destructive" data-testid="pay-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Betalingslenken kan ikke brukes</AlertTitle><AlertDescription>{error} Kontakt avsenderen av fakturaen for en ny lenke, eller betal til kontonummeret på fakturaen.</AlertDescription></Alert>
      </Shell>
    );
  }
  if (!view) {
    return <Shell><div className="space-y-4" data-testid="pay-loading"><Skeleton className="h-5 w-40" /><Skeleton className="h-9 w-64" /><Skeleton className="h-24" /><Skeleton className="h-11" /></div></Shell>;
  }

  const inv = view.invoice || {};
  const issuer = view.issuer || {};
  const accent = issuer.accent_color;
  const isPaid = view.status === "paid";
  const isCredited = view.status === "credited";
  const isOpen = view.status === "open";
  const unavailable = view.status === "unavailable";
  const latest = view.latest_payment;
  const failed = isReturn && !isPaid && FINAL_BAD.includes(view.payment_state);
  const pending = isReturn && !isPaid && !failed;

  return (
    <Shell accent={accent}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground" data-testid="pay-issuer">{issuer.name || "Faktura"}{issuer.org_number ? ` · Org.nr ${formatOrg(issuer.org_number)}` : ""}</p>
      <h1 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="pay-title">Faktura {inv.number_display}</h1>
      {inv.customer_name ? <p className="text-sm text-muted-foreground mt-0.5" data-testid="pay-customer">Til {inv.customer_name}</p> : null}

      <div className="mt-5 rounded-xl bg-secondary px-4 py-3">
        <p className="text-xs text-muted-foreground">{isPaid ? "Betalt" : "Å betale"}</p>
        <p className="font-heading text-3xl sm:text-4xl font-semibold tnum" data-testid="pay-amount">{formatKr(isPaid ? inv.total : inv.outstanding)}</p>
        {!isPaid && inv.paid_amount > 0 ? <p className="text-xs text-muted-foreground tnum">av {formatKr(inv.total)} · {formatKr(inv.paid_amount)} allerede innbetalt</p> : null}
      </div>

      <div className="mt-3 divide-y">
        <Row label="Fakturadato" value={formatDate(inv.issue_date)} testId="pay-issue-date" />
        <Row label="Forfallsdato" value={formatDate(inv.due_date)} testId="pay-due-date" />
        {inv.kid ? <Row label="KID" value={inv.kid} testId="pay-kid" /> : null}
      </div>

      <div className="mt-5 space-y-3">
        {isPaid ? (
          <StatusBanner tone="status-betalt" icon={CheckCircle2} title="Fakturaen er betalt" testId="pay-status-paid">Takk! {latest?.method ? `Betalt med ${latest.method === "CARD" ? "kort" : "Vipps"}. ` : ""}Du trenger ikke gjøre noe mer.</StatusBanner>
        ) : isCredited ? (
          <StatusBanner tone="status-avvist" icon={Info} title="Fakturaen er kreditert" testId="pay-status-credited">Denne fakturaen er annullert av avsenderen og skal ikke betales.</StatusBanner>
        ) : pending ? (
          <StatusBanner tone="status-ma_kontrolleres" icon={checking ? Loader2 : Info} title={checking ? "Sjekker betalingen hos Vipps …" : "Vi har ikke fått bekreftelse ennå"} testId="pay-status-pending">
            {checking ? "Dette tar vanligvis noen sekunder." : "Har du fullført betalingen i Vipps, blir fakturaen oppdatert så snart Vipps bekrefter. Du kan sjekke igjen eller prøve på nytt."}
          </StatusBanner>
        ) : failed ? (
          <StatusBanner tone="status-forfalt" icon={AlertTriangle} title="Betalingen ble ikke gjennomført" testId="pay-status-failed">{vippsState(view.payment_state).label}. Ingen penger er trukket. Du kan prøve igjen under.</StatusBanner>
        ) : unavailable ? (
          <StatusBanner tone="status-avvist" icon={Info} title="Vipps-betaling er ikke tilgjengelig for denne fakturaen" testId="pay-status-unavailable">Betal til kontonummeret under, og merk betalingen med fakturanummer{inv.kid ? " eller KID" : ""}.</StatusBanner>
        ) : null}

        {pending && checking ? <div className="flex justify-center py-1"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : null}
        {pending && !checking ? (
          <Button variant="outline" className="w-full h-11" onClick={recheck} data-testid="pay-recheck-button"><RefreshCw className="h-4 w-4 mr-2" /> Sjekk igjen</Button>
        ) : null}

        {(isOpen && (!isReturn || failed || (pending && !checking))) ? (
          <div className="space-y-2" data-testid="pay-methods">
            {startError ? <p className="text-sm text-destructive" role="alert" data-testid="pay-start-error">{startError}</p> : null}
            {(view.methods || []).map((m) => (
              <Button key={m.value} size="lg" variant={m.value === "WALLET" ? "default" : "outline"} className={cn("w-full h-12 text-base", m.value === "WALLET" && "bg-[#FF5B24] hover:bg-[#E64F1C] text-white")} onClick={() => start(m.value)} disabled={!!starting} data-testid={`pay-method-${m.value.toLowerCase()}`}>
                {starting === m.value ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : m.value === "WALLET" ? <Smartphone className="h-5 w-5 mr-2" /> : <CreditCard className="h-5 w-5 mr-2" />}
                {m.label}
              </Button>
            ))}
            {view.environment === "test" ? <p className="text-[11px] text-center text-muted-foreground" data-testid="pay-test-badge">Testmiljø – ingen ekte penger trekkes.</p> : null}
          </div>
        ) : null}

        {isReturn && isPaid ? <Button variant="outline" className="w-full h-11" onClick={() => navigate(`/betal/${token}`, { replace: true })} data-testid="pay-back-button">Vis fakturaen</Button> : null}
      </div>

      {!isPaid && !isCredited && issuer.bank_account ? (
        <div className="mt-6 rounded-xl border px-4 py-3 text-sm" data-testid="pay-bank-fallback">
          <p className="flex items-center gap-2 font-medium"><Landmark className="h-4 w-4 text-muted-foreground" /> Eller betal i nettbanken</p>
          <div className="mt-1 divide-y">
            <Row label="Kontonummer" value={formatAccount(issuer.bank_account)} testId="pay-bank-account" />
            {inv.kid ? <Row label="KID" value={inv.kid} /> : <Row label="Melding" value={`Faktura ${inv.number_display}`} />}
            <Row label="Beløp" value={formatKr(inv.outstanding)} strong />
          </div>
        </div>
      ) : null}
    </Shell>
  );
}
